// @flow
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';

export type SlotShift = {
    startTime: string,
    endTime: string
};

const TIME_10_PM = '22:00';
const TIME_DAY_END = '23:59';

export type RescheduleConfig = {
    staticSlots: Array<SlotShift>,
    allowReschedule: boolean,
    slotWidthMins: number,
    slotGapMins: number,
    overlapSlots: boolean,
    shift: SlotShift
};

export const SLOT_GENERATION_TYPE = Object.freeze({
    manual: 'manual',
    auto: 'auto'
});

export const getRadioButtonOptions = (
    t: (str: string) => string
): Array<{ key: string, value: string }> => [
    {
        key: SLOT_GENERATION_TYPE.manual,
        value: t('radioOptions.staticSlots')
    },
    {
        key: SLOT_GENERATION_TYPE.auto,
        value: t('radioOptions.autoGenSlots')
    }
];

export const getSlotLabel = (slot: SlotShift): string =>
    `${slot.startTime} - ${slot.endTime}`;

export const generateSlots = (config: RescheduleConfig): Array<SlotShift> => {
    const {
        shift: { startTime, endTime },
        slotWidthMins,
        overlapSlots,
        slotGapMins
    } = config;

    const toMoment = (time: string) =>
        TimeUtils.getMoment(time, DateTimeFormat.HTML5_FMT_TIME);

    const getAddedTime = (time: string, span: number) =>
        toMoment(time).add(span, 'm').format(DateTimeFormat.HTML5_FMT_TIME);

    let slots = [];
    let slotStartTime = startTime;
    let slotEndTime = getAddedTime(slotStartTime, slotWidthMins);

    // generate slots according to start time, slot width and slot gap
    while (
        TimeUtils.isAfter(toMoment(endTime), toMoment(slotEndTime)) &&
        TimeUtils.isAfter(toMoment(slotEndTime), toMoment(slotStartTime))
    ) {
        slots = [...slots, { startTime: slotStartTime, endTime: slotEndTime }];
        const lastTime = overlapSlots ? slotStartTime : slotEndTime;
        slotStartTime = getAddedTime(lastTime, slotGapMins);
        slotEndTime = getAddedTime(slotStartTime, slotWidthMins);
    }

    return slots;
};

type SlotConfig = {
    startTime: string,
    endTime: string,
    label: string
};

export const getSortedSlots = (
    slots: Array<{ startTime: string, endTime: string }>
): Array<SlotConfig> => {
    return slots
        .map(slot => {
            return {
                ...slot,
                label: getSlotLabel(slot)
            };
        })
        .sort((slot1, slot2) => {
            const slot1StartTime = TimeUtils.getMoment(
                slot1.startTime,
                DateTimeFormat.HTML5_FMT_TIME
            );
            const slot2StartTime = TimeUtils.getMoment(
                slot2.startTime,
                DateTimeFormat.HTML5_FMT_TIME
            );

            return TimeUtils.isAfter(slot1StartTime, slot2StartTime) ? 1 : -1;
        });
};

const getNearestEvenHour = (time: string): string => {
    const [hourString] = time.split(':').map(str => str.trim());

    const evenHour =
        Number(hourString) % 2 === 0 ? hourString : `${Number(hourString) - 1}`;

    return `${evenHour.length === 1 ? `0${evenHour}` : evenHour}:00`;
};

export const getGroupedSlots = (
    slots: Array<SlotConfig>
): Array<{ groupLabel: string, slots: Array<SlotConfig> }> => {
    if (!slots.length) {
        return [];
    }
    const [firstSlot] = slots;
    const lastSlot = slots[slots.length - 1];

    const { startTime: startSlotTime } = firstSlot;
    const { startTime: endSlotTime } = lastSlot;

    const nearestStartEvenHour = getNearestEvenHour(startSlotTime);
    const nearestEndEvenHour = getNearestEvenHour(endSlotTime);

    const toMoment = (time: string) =>
        TimeUtils.getMoment(time, DateTimeFormat.HTML5_FMT_TIME);

    const getNextGroupStartTime = (time: string): string => {
        return TimeUtils.getMoment(time, DateTimeFormat.HTML5_FMT_TIME)
            .add(2, 'h')
            .format(DateTimeFormat.HTML5_FMT_TIME);
    };

    const groups = [];
    let groupStartTime = nearestStartEvenHour;

    // create groups with a span of 2hrs
    while (
        TimeUtils.isSameOrAfter(
            toMoment(nearestEndEvenHour),
            toMoment(groupStartTime)
        )
    ) {
        groups.push({ time: groupStartTime, label: groupStartTime });
        // break loop after 10:00 PM
        if (TimeUtils.isSame(toMoment(TIME_10_PM), toMoment(groupStartTime))) {
            break;
        }
        groupStartTime = getNextGroupStartTime(groupStartTime);
    }

    // put slots in respective groups
    return groups.map(({ time, label }) => {
        const groupStartTime = time;
        const groupEndTime = TimeUtils.isSame(
            toMoment(TIME_10_PM),
            toMoment(groupStartTime)
        )
            ? TIME_DAY_END
            : getNextGroupStartTime(groupStartTime);

        const groupedSlots = slots.filter(({ startTime }) => {
            return (
                TimeUtils.isSameOrAfter(
                    toMoment(startTime),
                    toMoment(groupStartTime)
                ) &&
                TimeUtils.isAfter(toMoment(groupEndTime), toMoment(startTime))
            );
        });

        return {
            groupLabel: label,
            slots: groupedSlots
        };
    });
};
